import { useRouterState } from '@tanstack/react-router';
import type { JSX } from 'react';

import { Button } from '@/components/ui/button.js';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog.js';
import { useUiStore } from '@/features/settings/ui-store.js';

import { HintContent } from './HintContent.js';
import { HINTS, INTRO, type HintNode } from './hint-config.js';
import {
  dismissIntro,
  markIntroSeen,
  skipAllHints,
  useHintState,
} from './hint-store.js';

const INTRO_HINT: HintNode = {
  id: 'onboarding.intro',
  title: INTRO.title,
  body: INTRO.body,
  /** Video-only under the title; body lives in DialogDescription to avoid a duplicate paragraph. */
  mode: 'video',
  videoSrc: INTRO.videoSrc,
  videoAspect: INTRO.videoAspect ?? 'square',
};

/**
 * First-run intro modal. Shows while the intro is neither seen nor dismissed for this session. The
 * primary button prefills the composer with the suggested question (nothing is sent).
 *
 * Only a deliberate finish is persisted: Get started, or Skip intro (which skips the whole tour).
 * The X, Esc, and a backdrop click close the modal for this session only, so a reload while the
 * intro is still open brings it back.
 *
 * The height cap leaves the top of the viewport free: the "new version is ready" banner is a
 * portal pinned to `top-4`, and a full-height centered modal ran straight under it.
 */
export function OnboardingIntro(): JSX.Element | null {
  const { introSeen, introDismissed } = useHintState();
  const pathname = useRouterState({
    select: (routerState) => routerState.location.pathname,
  });
  const setComposerPrefill = useUiStore((state) => state.setComposerPrefill);

  if (introSeen || introDismissed) return null;

  /** Prefills the composer and closes the intro for good. */
  function handleGetStarted(): void {
    markIntroSeen({ withPrefill: true, path: pathname });
    setComposerPrefill(INTRO.suggestedPrompt);
  }

  /** Skips the whole onboarding — the intro and every hint beacon — without prefilling. */
  function handleSkip(): void {
    markIntroSeen();
    skipAllHints(Object.keys(HINTS));
  }

  return (
    <Dialog open onOpenChange={(open) => !open && dismissIntro()}>
      <DialogContent className="max-w-xl max-h-[calc(var(--app-height,100dvh)-14rem)]">
        <DialogHeader>
          <DialogTitle>{INTRO.title}</DialogTitle>
          <DialogDescription>{INTRO.body}</DialogDescription>
        </DialogHeader>
        <HintContent
          hint={INTRO_HINT}
          videoClassName="max-h-[min(30vh,20rem)]"
        />
        <p className="rounded-md border border-border bg-muted/40 px-3 py-2 text-sm text-muted-foreground">
          Starter question:{' '}
          <span className="font-medium text-foreground">
            &ldquo;{INTRO.suggestedPrompt}&rdquo;
          </span>
        </p>
        <DialogFooter className="gap-2 sm:flex-row sm:items-center sm:justify-between">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="text-muted-foreground hover:text-foreground"
            onClick={handleSkip}
          >
            Skip intro
          </Button>
          <Button type="button" onClick={handleGetStarted}>
            {INTRO.primaryButtonLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
